import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { STORAGE_KEYS, storage } from './storage';

/**
 * The look of the app. `system`, `light` and `dark` are the original "evidence console" look; the others are complete looks of their
 * own (a skin) on top of a light or dark base, so every `dark:` style still applies to the dark ones:
 * - `fosforlu`: a marked-up test plan — paper white, ink blue, highlighter strokes (light).
 * - `menteem`: the look of menteem.com.tr — navy and amber on soft slate, white rounded cards, Plus Jakarta Sans (light).
 */
export type ThemePref = 'system' | 'light' | 'dark' | 'fosforlu' | 'menteem';
export type Skin = 'fosforlu' | 'menteem';

export const THEMES: { id: ThemePref; base: 'light' | 'dark' | 'system'; skin: Skin | null }[] = [
  { id: 'system', base: 'system', skin: null },
  { id: 'light', base: 'light', skin: null },
  { id: 'dark', base: 'dark', skin: null },
  { id: 'fosforlu', base: 'light', skin: 'fosforlu' },
  { id: 'menteem', base: 'light', skin: 'menteem' },
];

// A look that no longer exists (Ozalit was replaced by Menteem) falls back to the system's.
const KNOWN = new Set<string>(THEMES.map((t) => t.id));
const listeners = new Set<() => void>();
const stored = storage.get(STORAGE_KEYS.theme);
let pref: ThemePref = stored && KNOWN.has(stored) ? (stored as ThemePref) : 'system';

function systemDark(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

/** Light or dark, after `system` is resolved. */
export function resolvedBase(value: ThemePref = pref): 'light' | 'dark' {
  const base = THEMES.find((t) => t.id === value)?.base ?? 'system';
  return base === 'system' ? (systemDark() ? 'dark' : 'light') : base;
}

function apply() {
  const root = document.documentElement;
  root.dataset.theme = resolvedBase();
  const skin = THEMES.find((t) => t.id === pref)?.skin;
  if (skin) root.dataset.skin = skin;
  else delete root.dataset.skin;
}

function reducedMotion(): boolean {
  return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Switches the look. With `from` (where the person clicked), the new look grows out of that point as a circle — the one moment of motion
 * that answers the choice; without View Transitions or with reduced motion it simply changes.
 */
export function setThemePref(next: ThemePref, from?: { x: number; y: number }) {
  const change = () => {
    pref = next;
    storage.set(STORAGE_KEYS.theme, next);
    apply();
    listeners.forEach((l) => l());
  };
  const doc = document as Document & { startViewTransition?: (cb: () => void) => { ready: Promise<void> } };
  if (!from || !doc.startViewTransition || reducedMotion() || next === pref) {
    change();
    return;
  }
  const transition = doc.startViewTransition(change);
  const radius = Math.hypot(Math.max(from.x, window.innerWidth - from.x), Math.max(from.y, window.innerHeight - from.y));
  void transition.ready.then(() => {
    document.documentElement.animate(
      { clipPath: [`circle(0px at ${from.x}px ${from.y}px)`, `circle(${radius}px at ${from.x}px ${from.y}px)`] },
      { duration: 520, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', pseudoElement: '::view-transition-new(root)' },
    );
  }).catch(() => undefined);
}

export function useTheme() {
  const value = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => pref,
  );
  useEffect(() => {
    apply();
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = () => {
      if (pref === 'system') {
        apply();
        listeners.forEach((l) => l());
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const cycle = useCallback((from?: { x: number; y: number }) => {
    const i = THEMES.findIndex((t) => t.id === value);
    setThemePref(THEMES[(i + 1) % THEMES.length].id, from);
  }, [value]);
  return { pref: value, resolved: resolvedBase(value), setPref: setThemePref, cycle };
}
